/**
 * Centralized client-side error reporting.
 *
 * Provides:
 *   - A breadcrumb ring-buffer (last 20 actions before a crash)
 *   - reportError(payload) — POST to /api/client_errors.php
 *
 * Import this module once (index.js) to start breadcrumb collection.
 * Import reportError anywhere you need to manually report.
 */

// ---------------------------------------------------------------------------
// Build identity — injected by CRA via .env or package.json homepage
// ---------------------------------------------------------------------------
const BUILD_VERSION =
  process.env.REACT_APP_VERSION ||
  process.env.REACT_APP_GIT_SHA ||
  null;

// ---------------------------------------------------------------------------
// Breadcrumb ring-buffer
// ---------------------------------------------------------------------------
const MAX_CRUMBS = 20;
const _crumbs = [];

function addCrumb(type, message, data = null) {
  const crumb = {
    t:    Date.now(),   // epoch ms — compact, exact
    type,
    msg:  String(message).slice(0, 200),
  };
  if (data) crumb.data = data;
  _crumbs.push(crumb);
  if (_crumbs.length > MAX_CRUMBS) _crumbs.shift();
}

/** Returns a snapshot of current breadcrumbs (newest last). */
export function getBreadcrumbs() {
  return _crumbs.slice();
}

// ---------------------------------------------------------------------------
// Noise filters — Instagram IAB, aborted fetches, blocked analytics, etc.
// ---------------------------------------------------------------------------
const IGNORABLE_ERROR_MESSAGE_PATTERNS = [
  /^ResizeObserver loop/,                    // harmless Chrome layout quirk
  /^Script error\.?$/i,                      // cross-origin error with no detail
  /ChunkLoadError/i,                         // handled by ErrorBoundary + reload
  /^Load failed$/i,                          // Safari network drop / page unload abort
  /Java object is gone/i,                    // Instagram Android IAB bridge
  /window\.webkit\.messageHandlers/i,        // Instagram iOS IAB bridge
  /evaluating ['"]window\.webkit/i,          // Safari wording for the same IAB bridge
  /Error invoking postMessage/i,             // in-app browser native bridge teardown
];

function isInAppBrowserUserAgent() {
  try {
    return /instagram|fbav|fban|fbios|fbsv|gsa|tiktok|line\//i.test(String(navigator.userAgent || ''));
  } catch {
    return false;
  }
}

/** Whether a client error is known third-party / transient noise (not a site bug). */
export function isIgnorableClientError(message, { name, sourceFile } = {}) {
  const msg = String(message || '');
  if (name === 'AbortError') return true;
  if (sourceFile && /iabjs:\/\//i.test(String(sourceFile))) return true;
  if (IGNORABLE_ERROR_MESSAGE_PATTERNS.some((re) => re.test(msg))) return true;
  // Instagram/Facebook iOS in-app browser injects native bridge scripts that throw
  // when webkit.messageHandlers is missing — not a Saloni Designs bug.
  if (isInAppBrowserUserAgent() && /messageHandlers|postMessage|Java object is gone/i.test(msg)) {
    return true;
  }
  return false;
}

/** Normalize global error event text — Safari often puts detail on event.error.message. */
export function clientErrorMessageFromEvent(event) {
  const fromEvent = String(event?.message || '').trim();
  const fromError = String(event?.error?.message || '').trim();
  if (fromEvent && fromEvent !== 'Script error.') return fromEvent;
  return fromError || fromEvent || 'Unknown error';
}

function isNoisyFetchFailure(url, err) {
  const urlStr = String(url || '');
  if (err && err.name === 'AbortError') return true;
  if (/google-analytics\.com|googletagmanager\.com|doubleclick\.net/i.test(urlStr)) {
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Auto-collect: navigation, user clicks, fetch errors
// ---------------------------------------------------------------------------
let _trackingInstalled = false;

export function installBreadcrumbTracking() {
  if (_trackingInstalled) return;
  _trackingInstalled = true;

  // ── Navigation ──────────────────────────────────────────────────────────
  function recordNav(url) {
    addCrumb('nav', url || window.location.pathname + window.location.search);
  }

  // Wrap history.pushState / replaceState (React Router uses these)
  ['pushState', 'replaceState'].forEach((method) => {
    const orig = window.history[method];
    window.history[method] = function (...args) {
      orig.apply(this, args);
      recordNav(typeof args[2] === 'string' ? args[2] : window.location.href);
    };
  });
  window.addEventListener('popstate', () => recordNav());

  // Initial page load
  recordNav(window.location.href);

  // ── User clicks on interactive elements ─────────────────────────────────
  document.addEventListener(
    'click',
    (e) => {
      const el = e.target && e.target.closest('button, a, [role="button"], input[type="submit"]');
      if (!el) return;
      const label =
        el.innerText?.trim().slice(0, 80) ||
        el.getAttribute('aria-label') ||
        el.getAttribute('href') ||
        el.tagName.toLowerCase();
      addCrumb('click', label, { tag: el.tagName.toLowerCase() });
    },
    { passive: true, capture: true }
  );

  // ── Fetch / XHR errors ───────────────────────────────────────────────────
  const origFetch = window.fetch;
  window.fetch = async function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '?';
    try {
      const res = await origFetch.apply(this, args);
      if (!res.ok && !isNoisyFetchFailure(url)) {
        addCrumb('fetch_error', `${res.status} ${res.statusText}`, {
          url: String(url).slice(0, 150),
        });
      }
      return res;
    } catch (err) {
      if (!isNoisyFetchFailure(url, err)) {
        addCrumb('fetch_error', String(err && err.message || err).slice(0, 150), {
          url: String(url).slice(0, 150),
        });
      }
      throw err;
    }
  };
}

// ---------------------------------------------------------------------------
// Device / environment snapshot (safe — no PII)
// ---------------------------------------------------------------------------
function getEnvContext() {
  const ctx = {
    build:      BUILD_VERSION,
    page_url:   typeof window !== 'undefined' ? window.location.href : null,
    referrer:   typeof document !== 'undefined' ? document.referrer || null : null,
    screen_w:   typeof window !== 'undefined' ? window.screen.width  : null,
    screen_h:   typeof window !== 'undefined' ? window.screen.height : null,
    viewport_w: typeof window !== 'undefined' ? window.innerWidth    : null,
    viewport_h: typeof window !== 'undefined' ? window.innerHeight   : null,
    dpr:        typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : null,
    online:     typeof navigator !== 'undefined' ? navigator.onLine : null,
  };
  // Connection info (Chrome only)
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      ctx.connection_type       = conn.effectiveType || null;
      ctx.connection_downlink   = conn.downlink      || null;
      ctx.connection_save_data  = conn.saveData      || false;
    }
  } catch (_) { /* ignore */ }
  return ctx;
}

// ---------------------------------------------------------------------------
// Core report function
// ---------------------------------------------------------------------------
export function reportError(payload) {
  try {
    const enriched = {
      ...payload,
      extra: {
        ...(payload.extra || {}),
        env:         getEnvContext(),
        breadcrumbs: getBreadcrumbs(),
      },
    };
    navigator.sendBeacon(
      '/api/client_errors.php',
      JSON.stringify(enriched)
    );
    // Optional Sentry side-channel (no SDK) when REACT_APP_SENTRY_DSN is set at build time.
    const dsn = String(process.env.REACT_APP_SENTRY_DSN || '').trim();
    if (dsn && !isIgnorableClientError(String(payload?.message || ''))) {
      reportToSentryDsn(dsn, enriched);
    }
  } catch (_) {
    /* ignore — best effort */
  }
}

/**
 * Minimal Sentry store event (no @sentry/react dependency).
 * @param {string} dsn
 * @param {object} payload
 */
function reportToSentryDsn(dsn, payload) {
  try {
    const m = /^https?:\/\/([^@]+)@([^/]+)\/(\d+)/i.exec(dsn);
    if (!m) return;
    const [, key, host, projectId] = m;
    const url = `https://${host}/api/${projectId}/store/`;
    const event = {
      message: String(payload.message || 'client error').slice(0, 2000),
      level: 'error',
      platform: 'javascript',
      logger: String(payload.error_type || 'javascript'),
      tags: { error_type: String(payload.error_type || 'unknown') },
      extra: payload.extra || {},
      request: { url: payload.url || (typeof window !== 'undefined' ? window.location.href : undefined) },
      exception: payload.stack
        ? {
            values: [
              {
                type: String(payload.error_type || 'Error'),
                value: String(payload.message || '').slice(0, 2000),
                stacktrace: { frames: [{ filename: 'client', function: payload.error_type || 'reportError' }] },
              },
            ],
          }
        : undefined,
    };
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Sentry-Auth': `Sentry sentry_version=7, sentry_client=saloni-js/1.0, sentry_key=${key}`,
      },
      body: JSON.stringify(event),
      keepalive: true,
      mode: 'cors',
    }).catch(() => {});
  } catch (_) {
    /* ignore */
  }
}
